import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';
import clsx from 'clsx';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';

const WARN_DAYS = 14;
const URGENT_DAYS = 3;

/**
 * Top-of-page banner shown when subscription is nearing expiry, suspended, or
 * already expired. Hidden when there are >14 days left or the data hasn't
 * loaded yet.
 *
 * Mounted in `Layout` so it appears on every CRM page.
 */
export const SubscriptionBanner: React.FC = () => {
  const { data, status, daysRemaining } = useSubscription();
  const { user } = useAuth();

  if (!data) return null;
  if (user?.role === 'super_admin') return null;

  // Suspended — always show
  if (status === 'suspended') {
    return (
      <div className="border-b border-danger/40 bg-danger-soft/60 px-7 py-2.5 text-sm text-danger flex items-center gap-2">
        <AlertTriangle size={14} className="shrink-0" />
        <span>Your subscription is suspended. Contact your administrator to reactivate.</span>
      </div>
    );
  }

  // Expired — always show
  if (status === 'expired' || (daysRemaining != null && daysRemaining === 0)) {
    return (
      <div className="border-b border-danger/40 bg-danger-soft/60 px-7 py-2.5 text-sm text-danger flex items-center gap-2">
        <AlertTriangle size={14} className="shrink-0" />
        <span>Your subscription has expired. Some features may be unavailable.</span>
      </div>
    );
  }

  // Active but nearing expiry
  if (daysRemaining != null && daysRemaining <= WARN_DAYS) {
    const urgent = daysRemaining <= URGENT_DAYS;
    return (
      <div
        className={clsx(
          'border-b px-7 py-2 text-sm flex items-center justify-between gap-3',
          urgent
            ? 'border-danger/40 bg-danger-soft/60 text-danger'
            : 'border-warning/40 bg-warning-soft/60 text-warning',
        )}
      >
        <div className="flex items-center gap-2">
          <Clock size={14} className="shrink-0" />
          <span>
            Your subscription expires in <strong>{daysRemaining} day{daysRemaining === 1 ? '' : 's'}</strong>.
            {urgent ? ' Renew soon to avoid service interruption.' : ''}
          </span>
        </div>
        <Link to="/settings/plan" className="underline hover:no-underline whitespace-nowrap">
          View plan
        </Link>
      </div>
    );
  }

  return null;
};
